import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Slider, { Settings as SliderProps } from 'react-slick';

type Props<T> = {
  items: T[];
  fixedItemWidth?: number;
  slidesToShow?: number;
  gap: number;
  keyExtractor: (item: T, index: number) => React.Key;
  renderItem: (item: T, index: number) => ReactNode;
  sliderProps?: SliderProps;
};

export const Carousel = <T,>({
  items,
  fixedItemWidth,
  slidesToShow: initialSlidesToShow,
  gap,
  keyExtractor,
  renderItem,
  sliderProps = {},
}: Props<T>) => {
  const sliderDefaults: SliderProps = {
    infinite: false,
    variableWidth: true,
    centerMode: false,
  };
  sliderProps = Object.assign({}, sliderDefaults, sliderProps);

  const sliderRef = useRef<Slider>(null);
  const [slideSettings, setSlideSettings] = useState({
    itemWidth: fixedItemWidth || 0, // arbitrary default to be overridden
    slidesToShow: initialSlidesToShow || 1,
  });

  useEffect(() => {
    const container = sliderRef.current?.innerSlider?.list;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      setSlideSettings((prevSlideSettings) => {
        const containerWidth = entries[0].contentRect.width;
        const changes = { ...prevSlideSettings };

        changes.itemWidth = fixedItemWidth
          ? fixedItemWidth
          : Math.floor(containerWidth / prevSlideSettings.slidesToShow) - gap;

        changes.slidesToShow = initialSlidesToShow
          ? initialSlidesToShow
          : Math.floor(containerWidth / (changes.itemWidth + gap));

        return changes;
      });
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [gap, fixedItemWidth, initialSlidesToShow]);

  // Balance margins when item width is not fixed
  const itemStyle: { marginLeft: number; marginRight?: number } = fixedItemWidth
    ? { marginLeft: gap }
    : { marginLeft: gap / 2, marginRight: gap / 2 };

  return (
    <Slider
      ref={sliderRef}
      slidesToScroll={slideSettings.slidesToShow}
      slidesToShow={slideSettings.slidesToShow}
      {...sliderProps}
    >
      {items.map((item, index) => (
        <div
          key={keyExtractor(item, index)}
          style={{ width: slideSettings.itemWidth + gap }}
        >
          <div style={itemStyle}>{renderItem(item, index)}</div>
        </div>
      ))}
    </Slider>
  );
};
